import React, { useState } from "react";
import * as Mui from "@mui/material";
import { CheckRounded, CurrencyRupeeOutlined, FileDownloadOutlined, PendingActionsOutlined, RemoveRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Loader } from "components/Loader";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { EmptyRow, SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid } from "pages/PayRoll/Shared/statutoryUi";
import { REPORT_STATUS_LABELS, downloadPtOverview, downloadPtRegister, toneOf } from "./ptReportsApi";

// Overview (PT3) from GET reports/overview/: month totals, one row per
// report (register, payments, validation) and PT by site. Cards and rows
// open the matching tab. Download (xlsx / csv / pdf) gives the same
// numbers with the same filters.

const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };
const totalCellSx = { ...tableCellSx, fontWeight: designWeights.semibold, borderTop: `1px solid ${designColors.slate300}` };
const REPORT_NAMES = { register: "PT Register", payments: "Payments", validation: "Validation" };

const money = (value) => (value == null ? "—" : `₹${Number(value).toLocaleString("en-IN")}`);
const num = (value) => (value == null ? "—" : Number(value).toLocaleString("en-IN"));

export const PtOverviewTab = ({ domain, companyId, month, monthLabel, siteId, departmentId, overview, onRetry, goTo }) => {
  const [menu, setMenu] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const data = overview.data;

  if (overview.loading && !data) return <Loader info="Loading..." minHeight="40vh" />;
  if (overview.error && !data) {
    return (
      <RequestInfoBanner
        tone="danger"
        title="Couldn't load the PT overview."
        body={overview.error}
        action={<SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
      />
    );
  }
  if (!data) return null;

  const shortMonth = (data.month_label || monthLabel).slice(0, 3);
  const download = async (format, writer = downloadPtOverview) => {
    setMenu(null);
    setDownloading(true);
    try {
      await writer({ domain, companyId, month, siteId, departmentId, format });
    } catch (error) {
      showToast("error", error.message || "Couldn't download the file.");
    } finally {
      setDownloading(false);
    }
  };

  const openReport = (row) => {
    if (row.key === "register") goTo("register");
    else if (row.key === "payments") goTo("payments", row.period_id ? { periodId: row.period_id, kind: "view" } : {});
    else goTo("validation");
  };
  const actionLinks = (row) => {
    const link = (label, onClick) => (
      <Mui.Link
        key={label}
        component="button"
        underline="hover"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        sx={linkSx}
      >
        {label}
      </Mui.Link>
    );
    const links = [link("View", () => openReport(row))];
    if (row.key === "register") links.push(link("Download", () => download("xlsx", downloadPtRegister)));
    if (row.key === "payments" && row.period_id) links.push(link("Record payment", () => goTo("payments", { periodId: row.period_id, kind: "record" })));
    return (
      <Mui.Stack direction="row" spacing={0.75} alignItems="center" divider={<Mui.Box component="span" sx={{ color: designColors.slate400 }}>·</Mui.Box>}>
        {links}
      </Mui.Stack>
    );
  };

  return (
    <Mui.Stack spacing={2}>
      <StatCardGrid
        onChange={(key) => {
          if (key === "deducted") goTo("register");
          else if (key === "paying") goTo("register", { status: "deducted" });
          else if (key === "exempt") goTo("register", { status: "exempt" });
          else goTo("payments");
        }}
        cards={[
          { key: "deducted", title: `PT deducted (${shortMonth})`, value: money(data.cards.pt_deducted), icon: CurrencyRupeeOutlined, tone: "blue" },
          { key: "paying", title: "Employees paying", value: num(data.cards.employees_paying), icon: CheckRounded, tone: "green" },
          { key: "exempt", title: "Exempt", value: num(data.cards.exempt), icon: RemoveRounded, tone: "amber" },
          { key: "topay", title: "To pay now", value: money(data.cards.to_pay_now), icon: PendingActionsOutlined, tone: "pink" },
        ]}
      />
      {data.note ? <RequestInfoBanner body={data.note} /> : null}

      <Mui.Box sx={detailCardSx}>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
          <Mui.Typography sx={designType.sectionHeading}>Reports — {data.month_label || monthLabel}</Mui.Typography>
          <SecondaryButton startIcon={<FileDownloadOutlined />} onClick={(event) => setMenu(event.currentTarget)} disabled={downloading} sx={{ whiteSpace: "nowrap" }}>
            {downloading ? "Downloading…" : "Download overview"}
          </SecondaryButton>
          <Mui.Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
            <Mui.MenuItem onClick={() => download("xlsx")}>Excel (.xlsx)</Mui.MenuItem>
            <Mui.MenuItem onClick={() => download("csv")}>CSV</Mui.MenuItem>
            <Mui.MenuItem onClick={() => download("pdf")}>PDF</Mui.MenuItem>
          </Mui.Menu>
        </Mui.Stack>
        <SimpleTable
          fit
          heads={[
            { label: "Report", width: 130 },
            { label: "Site", width: 200 },
            { label: "Details", width: 320 },
            { label: "Status", width: 130 },
            { label: "Action", width: 190 },
          ]}
        >
          {data.reports.length ? (
            data.reports.map((row, index) => (
              <Mui.TableRow key={`${row.key}-${row.site}-${index}`} hover onClick={() => openReport(row)} sx={{ cursor: "pointer" }}>
                <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.medium }}>{REPORT_NAMES[row.key] || row.key}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{row.site || "—"}</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{row.details || "—"}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>
                  <ToneChip label={REPORT_STATUS_LABELS[row.status] || row.status} tone={toneOf(row.status)} />
                </Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{actionLinks(row)}</Mui.TableCell>
              </Mui.TableRow>
            ))
          ) : (
            <EmptyRow colSpan={5} text="No reports for this month." />
          )}
        </SimpleTable>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>Reports are live views of the finalised payroll. View any report on screen before downloading.</Mui.Typography>
      </Mui.Box>

      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={designType.sectionHeading}>PT by site — {data.month_label || monthLabel}</Mui.Typography>
        <Mui.Typography sx={{ ...designType.helperText, mt: 0.25, mb: 1.5 }}>What was deducted this month and when it must be paid</Mui.Typography>
        <SimpleTable
          fit
          heads={[
            { label: "Site", width: 170 },
            { label: "How PT is collected", width: 230 },
            { label: "Paying", width: 70, align: "right" },
            { label: `Deducted in ${shortMonth} (₹)`, width: 130, align: "right" },
            { label: "Period so far (₹)", width: 120, align: "right" },
            { label: "Payment due", width: 170 },
            { label: "Status", width: 200 },
          ]}
        >
          {data.by_site.length ? (
            data.by_site.map((row) => (
              <Mui.TableRow
                key={row.id}
                hover={Boolean(row.period_id)}
                onClick={row.period_id ? () => goTo("payments", { periodId: row.period_id, kind: "view" }) : undefined}
                sx={{ cursor: row.period_id ? "pointer" : "default" }}
              >
                <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.site}</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{row.how_collected || "—"}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{num(row.paying)}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{num(row.deducted_this_month)}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{num(row.period_so_far)}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{row.payment_due_label || "—"}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{row.status_label ? <ToneChip label={row.status_label} tone={toneOf(row.status)} /> : "—"}</Mui.TableCell>
              </Mui.TableRow>
            ))
          ) : (
            <EmptyRow colSpan={7} text="No sites for this month." />
          )}
          {data.by_site.length ? (
            <Mui.TableRow>
              <Mui.TableCell sx={totalCellSx}>Total</Mui.TableCell>
              <Mui.TableCell sx={totalCellSx} />
              <Mui.TableCell align="right" sx={totalCellSx}>{num(data.total.paying)}</Mui.TableCell>
              <Mui.TableCell align="right" sx={totalCellSx}>{num(data.total.deducted_this_month)}</Mui.TableCell>
              <Mui.TableCell sx={totalCellSx} />
              <Mui.TableCell sx={totalCellSx} />
              <Mui.TableCell sx={totalCellSx} />
            </Mui.TableRow>
          ) : null}
        </SimpleTable>
      </Mui.Box>
    </Mui.Stack>
  );
};
